import { useCallback } from 'react';
import { api } from '../lib/api';
import { formatAgo, formatBytes, formatCount, formatDate } from '../lib/format';
import { go, href } from '../lib/route';
import { useLoad } from '../lib/useLoad';
import { Loading, Pager, Pill, Search } from '../components/bits';
import { PageHead } from '../components/Shell';

interface Props {
  readonly token: string;
  readonly query: URLSearchParams;
}

const SORTS: readonly { readonly value: string; readonly label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'seen', label: 'Last seen' },
  { value: 'email', label: 'Email' },
  { value: 'storage', label: 'Storage' },
];

export const Accounts = ({ token, query }: Props) => {
  const q = query.get('q') ?? '';
  const sort = query.get('sort') ?? 'newest';
  const offset = Number(query.get('offset')) || 0;

  const loaded = useLoad(
    () => api.listAccounts(token, { q, sort, offset }),
    [token, q, sort, offset],
  );

  const onSearch = useCallback((next: string) => go('/accounts', { q: next, sort }), [sort]);
  const now = Date.now();

  return (
    <>
      <PageHead
        eyebrow="Accounts"
        title="Everyone who has signed up"
        actions={
          <>
            <Search value={q} placeholder="Email or name" onSearch={onSearch} />
            <label className="field field-inline">
              <span className="visually-hidden">Sort</span>
              <select
                className="select"
                value={sort}
                onChange={(event) => go('/accounts', { q, sort: event.target.value })}
              >
                {SORTS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </>
        }
      />

      <Loading loaded={loaded}>
        {(page) => (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Account</th>
                    <th scope="col">Joined</th>
                    <th scope="col">Last seen</th>
                    <th scope="col" className="num">
                      Frames
                    </th>
                    <th scope="col" className="num">
                      Member of
                    </th>
                    <th scope="col" className="num">
                      Albums
                    </th>
                    <th scope="col" className="num">
                      Pictures
                    </th>
                    <th scope="col" className="num">
                      Storage
                    </th>
                    <th scope="col">State</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((account) => {
                    const locked = account.lockedUntil !== null && account.lockedUntil > now;
                    return (
                      <tr key={account.id}>
                        <td>
                          <a className="ref" href={href(`/accounts/${account.id}`)}>
                            <span className="ref-name">{account.name}</span>
                            <span className="ref-meta">{account.email}</span>
                          </a>
                        </td>
                        <td>{formatDate(account.createdAt)}</td>
                        <td>{formatAgo(account.lastSeenAt, now)}</td>
                        <td className="num">{formatCount(account.devicesOwned)}</td>
                        <td className="num">{formatCount(account.memberships)}</td>
                        <td className="num">{formatCount(account.albums)}</td>
                        <td className="num">{formatCount(account.assets)}</td>
                        <td className="num">{formatBytes(account.assetBytes)}</td>
                        <td>
                          {account.isAdmin && <Pill tone="ok">admin</Pill>}
                          {locked && <Pill tone="bad">locked</Pill>}
                          {!locked && account.failedCount > 0 && (
                            <Pill tone="wait">{account.failedCount} failed</Pill>
                          )}
                          {account.liveSessions > 0 && (
                            <Pill>{account.liveSessions} session{account.liveSessions === 1 ? '' : 's'}</Pill>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager page={page} noun="accounts" onOffset={(next) => go('/accounts', { q, sort, offset: next })} />
          </>
        )}
      </Loading>
    </>
  );
};
